import { lazy, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { CloudSyncProvider, SyncButton } from "@/components/CloudSyncProvider";
import StoreRevisionDebugOverlay from "@/components/StoreRevisionDebugOverlay";
import LazyRouteBoundary from "@/components/LazyRouteBoundary";
import GlobalLoginContextChoiceHost from "@/components/GlobalLoginContextChoiceHost";
import HardGateGuard from "@/components/HardGateGuard";
import TenantProvider from "@/components/TenantProvider";
import { HostRootSwitch, TenantPathCanonicalizer } from "@/components/TenantHostRouter";
import { startFlashSaleRotationScheduler, stopFlashSaleRotationScheduler } from "@/stores/flashSaleStore";
import Index from "./pages/Index";
// AuthGate is eagerly imported: a lazy chunk that fails/stalls on a cold load
// leaves the sign-in screen blank until a manual refresh.
import AuthGate from "./pages/AuthGate";

const Admin = lazy(() => import("./pages/Admin"));
const MergeChecklist = lazy(() => import("./pages/MergeChecklist"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const TrackOrder = lazy(() => import("./pages/TrackOrder"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const SwapPage = lazy(() => import("./pages/Swap"));
const OwnerLogin = lazy(() => import("./pages/OwnerLogin"));
const ReviewOrder = lazy(() => import("./pages/ReviewOrder"));
const CustomerAccount = lazy(() => import("./pages/CustomerAccount"));
const AdminTestLogin = lazy(() => import("./pages/AdminTestLogin"));
const NotFound = lazy(() => import("./pages/NotFound"));
const StoreManagerSignup = lazy(() => import("./pages/StoreManagerSignup"));
const StaffSignup = lazy(() => import("./pages/StaffSignup"));
const TenantStorefront = lazy(() => import("./pages/TenantStorefront"));
const StoresDirectory = lazy(() => import("./pages/StoresDirectory"));

const queryClient = new QueryClient();

const App = () => {
  useEffect(() => {
    startFlashSaleRotationScheduler();
    return () => stopFlashSaleRotationScheduler();
  }, []);
  return (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <LazyRouteBoundary>
          <TenantProvider>
            <CloudSyncProvider>
              <HardGateGuard>
            <Routes>
              <Route path="/" element={<HostRootSwitch><Index /></HostRootSwitch>} />
              <Route path="/admin/test-login" element={<AdminTestLogin />} />
              <Route path="/admin/merge-check" element={<MergeChecklist />} />
              <Route path="/admin/*" element={<Admin />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/track" element={<TrackOrder />} />
              <Route path="/swap" element={<SwapPage />} />
              <Route path="/review" element={<ReviewOrder />} />
              <Route path="/account" element={<CustomerAccount />} />
              <Route path="/owner-access" element={<OwnerLogin />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/auth-gate" element={<AuthGate />} />
              <Route path="/store-manager/signup" element={<StoreManagerSignup />} />
              <Route path="/staff/signup" element={<StaffSignup />} />
              <Route path="/store/:slug" element={<TenantPathCanonicalizer><TenantStorefront /></TenantPathCanonicalizer>} />
              <Route path="/stores" element={<StoresDirectory />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
              </HardGateGuard>
              <SyncButton />
              <StoreRevisionDebugOverlay />
            </CloudSyncProvider>
          </TenantProvider>
        </LazyRouteBoundary>
        <GlobalLoginContextChoiceHost />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  );
};

export default App;
